import type { BookingStatus, ExcursionBooking } from "../api/excursions/Excursion.typed";
import type { LeaveRequest, LeaveType } from "../api/leave/Leave.typed";
import { LeaveStatus } from "../api/leave/Leave.typed";
import { getStatusChip } from "./leaveHelpers";
import { colorTokens } from "../styles/globalStyles";

/** Server-seeded global leave type (company_id null); hourly planning unavailability. */
export const GLOBAL_PLANNING_UNAVAILABILITY_LEAVE_TYPE_NAME = "Souhait d'Indisponibilité";

export function findGlobalUnavailabilityLeaveType(leaveTypes: LeaveType[]): LeaveType | undefined {
  return leaveTypes.find(
    (t) =>
      (t.CompanyID === undefined || t.CompanyID === null) &&
      t.Name === GLOBAL_PLANNING_UNAVAILABILITY_LEAVE_TYPE_NAME &&
      t.Unit === "hours",
  );
}

export function isPlanningGlobalUnavailabilityLeaveRequest(
  request: LeaveRequest,
  leaveTypes?: LeaveType[],
): boolean {
  if (request.type_name?.trim() === GLOBAL_PLANNING_UNAVAILABILITY_LEAVE_TYPE_NAME) {
    return true;
  }
  if (leaveTypes && leaveTypes.length > 0) {
    const g = findGlobalUnavailabilityLeaveType(leaveTypes);
    if (g != null && g.ID === request.type_id) {
      return true;
    }
  }
  return false;
}

export type LeaveMotifVisibilityOpts = {
  viewerUserId?: number | null;
  /** With matching demandeur: private indispo motif visible (Planning « moi »). */
  viewerHasPlanningMeView?: boolean;
};

/** Global indispo: motif if public, or private when viewer is demandeur and has planning.me.view. */
export function shouldDisplayLeaveMotifForUi(
  request: LeaveRequest,
  leaveTypes?: LeaveType[],
  opts?: LeaveMotifVisibilityOpts,
): boolean {
  if (!isPlanningGlobalUnavailabilityLeaveRequest(request, leaveTypes)) {
    return true;
  }
  if (request.reason_is_public === true) {
    return true;
  }
  if (
    opts?.viewerHasPlanningMeView === true &&
    opts.viewerUserId != null &&
    request.user_id === opts.viewerUserId
  ) {
    return true;
  }
  return false;
}

export const PLANNING_CROSS_TENANT_SLOT_LABEL = "Indisponible";

/**
 * One-line planning label for: other-tenant leave/booking, same-tenant global unavailability wish.
 * Without a display name (fully anonymized cross-tenant) → "Indisponible" only.
 */
export function formatPlanningUnavailableSlotLabel(displayName: string | undefined): string {
  const trimmed = displayName?.trim();
  if (!trimmed) {
    return PLANNING_CROSS_TENANT_SLOT_LABEL;
  }

  return `${formatPlanningUserShortName(trimmed)} - ${PLANNING_CROSS_TENANT_SLOT_LABEL}`;
}

/** Planning cards for API-driven unavailable slots (anonymized / indispo label / global indispo type). */
export const PLANNING_UNAVAILABLE_SLOT_BACKGROUND = colorTokens.bg.surfaceMuted;

/** Planning-all: anonymized slot (other tenant) or legacy zero user id. */
export function isPlanningLeaveCrossTenantSlot(request: LeaveRequest): boolean {
  return request.planning_anonymized === true || request.user_id === 0;
}

export function isPlanningBookingCrossTenantSlot(booking: ExcursionBooking): boolean {
  return booking.planning_anonymized === true || booking.user_id === 0;
}

/** Booking row is for another company than the tenant in context (x-company-id / session tenant). */
export function isPlanningBookingForeignCompany(
  booking: ExcursionBooking,
  viewerTenantCompanyId: number | undefined,
): boolean {
  return (
    booking.company_id != null &&
    viewerTenantCompanyId != null &&
    viewerTenantCompanyId > 0 &&
    booking.company_id !== viewerTenantCompanyId
  );
}

/** Leave row is stored under another company than the viewer tenant (linked user / planning parity). */
export function isPlanningLeaveForeignCompany(
  request: LeaveRequest,
  viewerTenantCompanyId: number | undefined,
): boolean {
  if (request.company_id == null) return false;
  // API redacts real foreign tenant id as 0 on linked-other-company rows.
  if (request.company_id === 0) return true;
  return (
    viewerTenantCompanyId != null &&
    viewerTenantCompanyId > 0 &&
    request.company_id !== viewerTenantCompanyId
  );
}

/** Client-side calendar narrowing when the role has multiple planning view rights (API uses the broadest list). */
export type PlanningCalendarScope = "all" | "company" | "me";

function planningEmployeeMapIsActive(map: Map<number, boolean> | undefined): boolean {
  return map != null && map.size > 0;
}

/** "Ma société" scope: subject user must have role.is_employee true (bookings may use API user_is_employee). */
function leaveSubjectIsEmployeeForCompanyScope(
  request: LeaveRequest,
  employeeByUserId: Map<number, boolean> | undefined,
): boolean {
  const mapActive = planningEmployeeMapIsActive(employeeByUserId);
  if (!mapActive) {
    return true;
  }

  return employeeByUserId!.get(request.user_id) === true;
}

function bookingSubjectIsEmployeeForCompanyScope(
  booking: ExcursionBooking,
  employeeByUserId: Map<number, boolean> | undefined,
): boolean {
  if (booking.user_is_employee === true) {
    return true;
  }

  if (booking.user_is_employee === false) {
    return false;
  }

  const mapActive = planningEmployeeMapIsActive(employeeByUserId);
  if (!mapActive) {
    return true;
  }

  return employeeByUserId!.get(booking.user_id) === true;
}

export function filterLeavesForPlanningCalendarScope(
  requests: LeaveRequest[],
  scope: PlanningCalendarScope,
  viewerUserId: number | undefined,
  viewerTenantCompanyId: number | undefined,
  employeeByUserId?: Map<number, boolean>,
): LeaveRequest[] {
  if (scope === "all" || viewerUserId == null) {
    return requests;
  }

  if (scope === "me") {
    return requests.filter((r) => r.user_id === viewerUserId);
  }

  return requests.filter((r) => {
    if (r.user_id === viewerUserId) {
      return true;
    }

    if (isPlanningLeaveCrossTenantSlot(r)) {
      return false;
    }

    if (isPlanningLeaveForeignCompany(r, viewerTenantCompanyId)) {
      return false;
    }

    return leaveSubjectIsEmployeeForCompanyScope(r, employeeByUserId);
  });
}

export function filterBookingsForPlanningCalendarScope(
  bookings: ExcursionBooking[],
  scope: PlanningCalendarScope,
  viewerUserId: number | undefined,
  viewerTenantCompanyId: number | undefined,
  employeeByUserId?: Map<number, boolean>,
): ExcursionBooking[] {
  if (scope === "all" || viewerUserId == null) {
    return bookings;
  }

  if (scope === "me") {
    return bookings.filter((b) => b.user_id === viewerUserId);
  }

  return bookings.filter((b) => {
    if (b.user_id === viewerUserId) {
      return true;
    }

    if (isPlanningBookingCrossTenantSlot(b)) {
      return false;
    }

    if (isPlanningBookingForeignCompany(b, viewerTenantCompanyId)) {
      return false;
    }

    return bookingSubjectIsEmployeeForCompanyScope(b, employeeByUserId);
  });
}

/**
 * Compact planning slot (muted card, shortened line): cross-tenant anonymized, or another company’s leave.
 * Global “Souhait d’Indisponibilité” is not tenant-scoped for display — same line as a normal hourly leave.
 */
export function isPlanningLeaveCompactSlot(
  request: LeaveRequest,
  viewerTenantCompanyId: number | undefined,
  leaveTypes?: LeaveType[],
): boolean {
  if (isPlanningLeaveCrossTenantSlot(request)) return true;
  if (!isPlanningLeaveForeignCompany(request, viewerTenantCompanyId)) return false;
  if (isPlanningGlobalUnavailabilityLeaveRequest(request, leaveTypes)) return false;
  return true;
}

/** Same palette as excursion booking cards for compact other-tenant slots. */
export function planningLeaveStatusCardColor(status: number): string {
  switch (status) {
    case LeaveStatus.PENDING:
    case LeaveStatus.CANCELLATION_REQUESTED:
      return colorTokens.status.warning;
    case LeaveStatus.APPROVED:
      return colorTokens.status.success;
    case LeaveStatus.REJECTED:
      return colorTokens.status.danger;
    case LeaveStatus.CANCELLED:
      return colorTokens.bg.surfaceMuted;
    default:
      return colorTokens.bg.surfaceMuted;
  }
}

/** Excursion booking row that represents planning unavailability (align with global indispo leave). */
export function isPlanningUnavailabilityExcursionBooking(booking: ExcursionBooking): boolean {
  const combined =
    `${booking.excursion_template ?? ""} ${booking.planning_slot_label ?? ""}`.toLowerCase();
  return (
    combined.includes("indisponibilité") ||
    combined.includes("indisponibilite") ||
    combined.includes("indisponible") ||
    combined.includes("unavailability")
  );
}

const PLANNING_BOOKING_STATUS_CARD_COLORS: Record<BookingStatus, string> = {
  pending: colorTokens.status.warning,
  validated: colorTokens.status.success,
  rejected: colorTokens.status.danger,
  cancelled: colorTokens.text.muted,
};

/** Single entry point for planning leave vs booking card backgrounds. */
export type PlanningSlotCardBackgroundSpec =
  | { kind: "unavailability" }
  | { kind: "leave"; compactSlot: boolean; typeColor: string; status: number }
  | { kind: "booking"; status: BookingStatus };

export function planningSlotCardBackgroundColor(
  _leaveTypes: LeaveType[] | undefined,
  _typesMap: Record<number, { Color: string; Name: string }>,
  spec: PlanningSlotCardBackgroundSpec,
): string {
  if (spec.kind === "unavailability") {
    return PLANNING_UNAVAILABLE_SLOT_BACKGROUND;
  }
  if (spec.kind === "leave") {
    return spec.compactSlot ? planningLeaveStatusCardColor(spec.status) : spec.typeColor;
  }

  return PLANNING_BOOKING_STATUS_CARD_COLORS[spec.status];
}

export function formatPlanningUserShortName(fullName: string | undefined): string {
  if (!fullName) return "Utilisateur";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return "Utilisateur";
  const firstName = parts[0];
  const lastParts = parts.slice(1);
  if (!lastParts.length) return firstName;
  const initials = lastParts
    .map((p) => p.trim().charAt(0))
    .filter(Boolean)
    .join("")
    .toUpperCase();
  if (!initials) return firstName;
  return `${firstName} ${initials}`;
}

export function planningDisplayNameOrUnavailable(
  fullName: string | undefined,
  crossTenant: boolean,
): string {
  return crossTenant ? PLANNING_CROSS_TENANT_SLOT_LABEL : formatPlanningUserShortName(fullName);
}

/** Tenant label when scope is limited to self: cartouche on the card body. */
export function leavePlanningCompanyCartouche(request: LeaveRequest): string | null {
  if (isPlanningLeaveCrossTenantSlot(request)) return null;
  const cn = request.company_name?.trim();
  return cn || null;
}

export function bookingPlanningCompanyCartouche(booking: ExcursionBooking): string | null {
  if (isPlanningBookingCrossTenantSlot(booking)) return null;
  const cn = booking.company_name?.trim();
  return cn || null;
}

export function planningBookingStatusEmoji(status: BookingStatus | undefined): string {
  switch (status) {
    case "pending":
      return "⏳";
    case "validated":
      return "✅";
    case "rejected":
      return "❌";
    case "cancelled":
      return "🚫";
    default:
      return "•";
  }
}

function formatPlanningBookingTimeSuffix(booking: ExcursionBooking): string {
  const time = [booking.start_time, booking.end_time].filter(Boolean).join("–");
  return time ? ` (${time})` : "";
}

/** Single-line label for a booking cell or day summary (emoji + payload label or fallback). */
export function formatPlanningBookingSlotMainText(
  booking: ExcursionBooking,
  getUserName?: (id: number) => string | undefined,
  viewerTenantCompanyId?: number,
  viewerUserId?: number,
): string {
  const crossTenant = isPlanningBookingCrossTenantSlot(booking);
  const foreignCompany = isPlanningBookingForeignCompany(booking, viewerTenantCompanyId);
  const ownCrossTenantBooking =
    (crossTenant || foreignCompany) && viewerUserId != null && booking.user_id === viewerUserId;
  const sameTenantUnav =
    isPlanningUnavailabilityExcursionBooking(booking) && !crossTenant && !foreignCompany;
  const nameRaw = booking.user_name?.trim() || getUserName?.(booking.user_id)?.trim();

  if ((!ownCrossTenantBooking && (crossTenant || foreignCompany)) || sameTenantUnav) {
    return formatPlanningUnavailableSlotLabel(nameRaw);
  }

  const emoji = planningBookingStatusEmoji(booking.status ?? undefined);
  const timeSuffix = formatPlanningBookingTimeSuffix(booking);
  const slotLabel = booking.planning_slot_label?.trim();
  const shortName = formatPlanningUserShortName(nameRaw);
  if (slotLabel) return `${emoji} ${slotLabel}${timeSuffix}`;
  return `${emoji} ${shortName} • ${booking.excursion_template ?? ""}${timeSuffix}`;
}

export function getLeavePlanningStatusDisplay(status: number): {
  emoji: string;
  lineStatusSuffix: string;
} {
  const chip = getStatusChip(status);
  const name = chip?.name;

  switch (status) {
    case LeaveStatus.PENDING:
      return { emoji: "⏳", lineStatusSuffix: "" };
    case LeaveStatus.APPROVED:
      return { emoji: "✅", lineStatusSuffix: "" };
    case LeaveStatus.REJECTED:
      return { emoji: "❌", lineStatusSuffix: "" };
    case LeaveStatus.CANCELLED:
      return { emoji: "🛑", lineStatusSuffix: name ? ` — ${name}` : "" };
    case LeaveStatus.CANCELLATION_REQUESTED:
      return { emoji: "🔄", lineStatusSuffix: name ? ` — ${name}` : "" };
    default:
      return { emoji: "•", lineStatusSuffix: name ? ` — ${name}` : "" };
  }
}

export const shouldShowStatusInPlanning = (status: number) => {
  return status === LeaveStatus.APPROVED || status === LeaveStatus.PENDING;
};

export const shouldShowTypeInPlanning = (_typeId: number) => {
  return true;
};

export const getRequestTypeColorForPlanning = (
  typeId: number,
  typesMap?: Record<number, LeaveType>,
) => {
  const color = typesMap?.[typeId]?.Color;
  return color || "#666";
};

/**
 * Compact HH:mm–HH:mm for an hourly leave. Leaves are TZ-agnostic: we read the UTC components of
 * `start_at` / `end_at` as wall clock, so "09:00 – 12:00" stays "09:00 – 12:00" for any viewer.
 */
export function formatPlanningLeaveHourRange(
  request: LeaveRequest,
  _companyTimeZone?: string | null,
): string {
  if (!request.start_at || !request.end_at) {
    return "";
  }

  try {
    const a = new Date(request.start_at);
    const b = new Date(request.end_at);

    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) {
      return "";
    }

    const opts: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "UTC",
    };
    const fmt = new Intl.DateTimeFormat("fr-FR", opts);
    return `${fmt.format(a)}–${fmt.format(b)}`;
  } catch {
    return "";
  }
}

function formatYmdToFrDate(ymd: string): string {
  const t = ymd.trim().slice(0, 10);
  const [y, m, d] = t.split("-").map((x) => parseInt(x, 10));
  if (!y || !m || !d || Number.isNaN(y)) return t;
  return new Date(y, m - 1, d).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Date + hours for anonymous leave tooltips (planning all / cross-tenant). */
export function formatPlanningLeaveAnonymousSlotDetail(
  request: LeaveRequest,
  companyTimeZone?: string | null,
): string {
  const hourRange = formatPlanningLeaveHourRange(request, companyTimeZone);
  if (hourRange) {
    const day = request.start_date?.trim().slice(0, 10);
    if (day) return `${formatYmdToFrDate(day)} · ${hourRange}`;
    return hourRange;
  }
  const a = request.start_date?.trim().slice(0, 10);
  const b = request.end_date?.trim().slice(0, 10);
  if (a && b) {
    if (a === b) return formatYmdToFrDate(a);
    return `${formatYmdToFrDate(a)} → ${formatYmdToFrDate(b)}`;
  }
  return "";
}

/** Date + time range for anonymous excursion booking tooltips. */
export function formatPlanningBookingAnonymousSlotDetail(booking: ExcursionBooking): string {
  const ymd = booking.date?.trim().slice(0, 10) ?? "";
  const parts: string[] = [];
  if (ymd) parts.push(formatYmdToFrDate(ymd));
  const t0 = booking.start_time?.trim();
  const t1 = booking.end_time?.trim();
  if (t0 && t1) parts.push(`${t0}–${t1}`);
  else if (t0) parts.push(t0);
  return parts.join(" · ");
}

export function bookingPlanningExtraTooltipText(
  booking: ExcursionBooking,
  viewerTenantCompanyId?: number,
): string | null {
  const crossTenant = isPlanningBookingCrossTenantSlot(booking);
  const foreignCompany = isPlanningBookingForeignCompany(booking, viewerTenantCompanyId);
  if (!crossTenant && !foreignCompany) return null;
  const detail = formatPlanningBookingAnonymousSlotDetail(booking).trim();
  return detail || null;
}

/** Tooltip title: status emoji + display name / slot label (no duplicate of the card’s main line). */
export function formatPlanningBookingCardTooltipHeader(
  booking: ExcursionBooking,
  getUserName?: (id: number) => string | undefined,
  viewerTenantCompanyId?: number,
  viewerUserId?: number,
): string {
  if (booking.planning_anonymized) {
    return formatPlanningUnavailableSlotLabel(
      booking.user_name?.trim() || getUserName?.(booking.user_id)?.trim(),
    );
  }

  const cross = isPlanningBookingCrossTenantSlot(booking);
  const foreign = isPlanningBookingForeignCompany(booking, viewerTenantCompanyId);
  const ownCrossTenantBooking =
    (cross || foreign) && viewerUserId != null && booking.user_id === viewerUserId;
  const sameTenantUnav = isPlanningUnavailabilityExcursionBooking(booking) && !cross && !foreign;
  const nameRaw = booking.user_name?.trim() || getUserName?.(booking.user_id)?.trim();

  if ((!ownCrossTenantBooking && (cross || foreign)) || sameTenantUnav) {
    return formatPlanningUnavailableSlotLabel(nameRaw);
  }

  const base = nameRaw || "Créneau";
  return base;
}

/** Multiline tooltip: date/time, excursion name, tenant — excludes title line and textual status chip. */
export function formatPlanningBookingCardTooltipBody(
  booking: ExcursionBooking,
  getUserName?: (id: number) => string | undefined,
  viewerTenantCompanyId?: number,
  viewerUserId?: number,
): string {
  if (booking.planning_anonymized) {
    const when = formatPlanningBookingAnonymousSlotDetail(booking).trim();
    return when ? `${PLANNING_CROSS_TENANT_SLOT_LABEL}\n${when}` : PLANNING_CROSS_TENANT_SLOT_LABEL;
  }

  const cross = isPlanningBookingCrossTenantSlot(booking);
  const foreign = isPlanningBookingForeignCompany(booking, viewerTenantCompanyId);
  const ownCrossTenantBooking =
    (cross || foreign) && viewerUserId != null && booking.user_id === viewerUserId;
  const compact = (cross || foreign) && !ownCrossTenantBooking;
  const lines: string[] = [];

  const st = booking.status;
  const statusEmoji = planningBookingStatusEmoji(st);
  const statusWord =
    st === "pending"
      ? "En attente"
      : st === "validated"
        ? "Validée"
        : st === "rejected"
          ? "Refusée"
          : st === "cancelled"
            ? "Annulée"
            : "—";
  lines.push(`${statusEmoji} ${statusWord}`);

  if (!compact) {
    const whenWhere = formatPlanningBookingAnonymousSlotDetail(booking).trim();
    if (whenWhere) {
      lines.push(whenWhere);
    }
  }

  if (!cross && !foreign) {
    const tmpl = booking.excursion_template?.trim();
    if (tmpl) {
      lines.push(`Prestation : ${tmpl}`);
    }
  }

  const comp = booking.company_name?.trim();
  if (comp) {
    lines.push(`Club : ${comp}`);
  }

  if (compact && lines.length === 0) {
    const whenFallback = formatPlanningBookingAnonymousSlotDetail(booking).trim();
    if (whenFallback) {
      lines.push(whenFallback);
    }
  }

  const extra = compact
    ? ""
    : (bookingPlanningExtraTooltipText(booking, viewerTenantCompanyId) ?? "");
  const core = lines.join("\n");
  return extra ? `${core}\n\n${extra}` : core;
}

export function formatPlanningLeaveCardTooltipHeader(
  request: LeaveRequest,
  getUserName?: (id: number) => string | undefined,
  viewerTenantCompanyId?: number,
  leaveTypes?: LeaveType[],
): string {
  const cross = isPlanningLeaveCrossTenantSlot(request);
  const globalUnav = isPlanningGlobalUnavailabilityLeaveRequest(request, leaveTypes);
  const compact = isPlanningLeaveCompactSlot(request, viewerTenantCompanyId, leaveTypes);
  const nameRaw = request.user_name?.trim() || getUserName?.(request.user_id)?.trim();

  if (cross || compact || globalUnav) {
    return formatPlanningUnavailableSlotLabel(nameRaw);
  }

  const base = nameRaw || "Créneau";
  return base;
}

/** Multiline tooltip for planning leave cards (full text when the card line is ellipsized). */
export function formatPlanningLeaveCardTooltipBody(
  request: LeaveRequest,
  typesMap: Record<number, { Color: string; Name: string }>,
  getUserName?: (id: number) => string | undefined,
  companyTimeZone?: string | null,
  viewerTenantCompanyId?: number,
  leaveTypes?: LeaveType[],
  motifOpts?: LeaveMotifVisibilityOpts,
): string {
  const compact = isPlanningLeaveCompactSlot(request, viewerTenantCompanyId, leaveTypes);
  const cross = isPlanningLeaveCrossTenantSlot(request);
  const parts: string[] = [];

  const { name: typeName } = resolveLeaveTypeDisplayForPlanning(request, typesMap);
  const hourRange = formatPlanningLeaveHourRange(request, companyTimeZone);
  const typeWithTime = hourRange ? `${typeName} (${hourRange})` : typeName;

  const { emoji, lineStatusSuffix } = getLeavePlanningStatusDisplay(request.status);
  let statusLine = `${emoji} ${typeWithTime}`;
  if (!compact) statusLine += lineStatusSuffix;
  parts.push(statusLine);

  const detail = formatPlanningLeaveAnonymousSlotDetail(request, companyTimeZone).trim();
  if (detail && !compact) {
    parts.push(detail);
  }

  if (!cross) {
    const cn = request.company_name?.trim();
    if (cn) {
      parts.push(`Club : ${cn}`);
    }
  }

  const motif = request.reason?.trim();
  if (motif && shouldDisplayLeaveMotifForUi(request, leaveTypes, motifOpts)) {
    parts.push(`Motif : ${motif}`);
  }

  return parts.filter(Boolean).join("\n");
}

const PLANNING_ANONYMIZED_SLOT_COLOR = colorTokens.bg.surfaceMuted;

/** Prefer API type fields (leave_types row for the request company); else current-tenant typesMap. */
export function resolveLeaveTypeDisplayForPlanning(
  request: LeaveRequest,
  typesMap: Record<number, { Color: string; Name: string }>,
): { name: string; color: string } {
  if (isPlanningLeaveCrossTenantSlot(request)) {
    return { name: "", color: PLANNING_ANONYMIZED_SLOT_COLOR };
  }

  const apiName = request.type_name?.trim();
  const apiColor = request.type_color?.trim();
  const mapEntry = typesMap[request.type_id];
  return {
    name: apiName || mapEntry?.Name || "—",
    color: apiColor || mapEntry?.Color || getRequestTypeColorForPlanning(request.type_id),
  };
}

/**
 * Single-line label for a leave cell or day summary.
 * Use `appendLeaveStatusSuffix` for list-style tooltips (cancelled / annulation demandée).
 */
export function formatPlanningLeaveSlotLine(
  request: LeaveRequest,
  typesMap: Record<number, { Color: string; Name: string }>,
  getUserName?: (id: number) => string | undefined,
  options?: {
    appendLeaveStatusSuffix?: boolean;
    companyTimeZone?: string | null;
    viewerTenantCompanyId?: number;
    leaveTypes?: LeaveType[];
  },
): string {
  const crossTenant = isPlanningLeaveCrossTenantSlot(request);
  const globalUnav = isPlanningGlobalUnavailabilityLeaveRequest(request, options?.leaveTypes);
  const compactSlot = isPlanningLeaveCompactSlot(
    request,
    options?.viewerTenantCompanyId,
    options?.leaveTypes,
  );
  const unavailableOneLine = crossTenant || compactSlot || globalUnav;
  const nameForUnavail = request.user_name?.trim() || getUserName?.(request.user_id)?.trim();

  if (unavailableOneLine) {
    return formatPlanningUnavailableSlotLabel(nameForUnavail);
  }

  const { emoji, lineStatusSuffix } = getLeavePlanningStatusDisplay(request.status);
  const hourRange = formatPlanningLeaveHourRange(request, options?.companyTimeZone);
  const hourSuffix = hourRange ? ` (${hourRange})` : "";
  const slotLabel = request.planning_slot_label?.trim();
  const displayName = request.user_name || getUserName?.(request.user_id);
  const shortName = planningDisplayNameOrUnavailable(displayName, false);
  const { name: typeName } = resolveLeaveTypeDisplayForPlanning(request, typesMap);
  const typeWithTime = hourRange ? `${typeName} (${hourRange})` : typeName;

  let main: string;
  if (slotLabel) main = `${emoji} ${slotLabel}${hourSuffix}`;
  else main = `${emoji} ${shortName} • ${typeWithTime}`;

  if (options?.appendLeaveStatusSuffix) main += lineStatusSuffix;
  return main;
}

export const filterRequestsForPlanning = (requests: LeaveRequest[], statusFilter?: number) => {
  if (!requests || !Array.isArray(requests)) {
    return [];
  }
  if (statusFilter !== undefined && statusFilter !== -1) {
    return requests.filter(
      (request) => request.status === statusFilter && shouldShowTypeInPlanning(request.type_id),
    );
  }
  return requests.filter(
    (request) =>
      shouldShowStatusInPlanning(request.status) && shouldShowTypeInPlanning(request.type_id),
  );
};
